import {
  DEDUCTION_POINTS,
  ELEMENT_BASE_POINTS,
  type MistakeSeverity,
  type ScoredElement,
} from '../../data/scoring'

export interface CriterionMarkState {
  criterionId: string
  severity: MistakeSeverity
}

export interface ElementMarkState {
  elementId: string
  /** True once the judge opened/edited this element. */
  reviewed: boolean
  forgotten: boolean
  criteria: CriterionMarkState[]
  /** Optional reviewer comment for this element only. */
  comment?: string
}

export interface ElementScoreResult {
  elementId: string
  points: number
  deductions: number
  forgotten: boolean
  reviewed: boolean
}

export interface PerformanceScoreResult {
  elementScores: ElementScoreResult[]
  rawTotal: number
  forgottenPenaltyApplied: boolean
  finalTotal: number
  maxTotal: number
  reviewedCount: number
  elementCount: number
}

export function deductionFor(severity: MistakeSeverity): number {
  return DEDUCTION_POINTS[severity]
}

export function scoreElement(
  element: ScoredElement,
  mark: ElementMarkState | undefined,
): ElementScoreResult {
  if (!mark) {
    return {
      elementId: element.id,
      points: ELEMENT_BASE_POINTS,
      deductions: 0,
      forgotten: false,
      reviewed: false,
    }
  }
  if (mark.forgotten) {
    return {
      elementId: element.id,
      points: 0,
      deductions: ELEMENT_BASE_POINTS,
      forgotten: true,
      reviewed: mark.reviewed,
    }
  }
  let deductions = 0
  for (const c of mark.criteria) {
    deductions += deductionFor(c.severity)
  }
  const points = Math.max(0, ELEMENT_BASE_POINTS - deductions)
  return {
    elementId: element.id,
    points,
    deductions: Math.min(deductions, ELEMENT_BASE_POINTS),
    forgotten: false,
    reviewed: mark.reviewed,
  }
}

export function scorePerformance(
  elements: ScoredElement[],
  marks: ElementMarkState[],
  maxTotal: number,
): PerformanceScoreResult {
  const byId = new Map(marks.map((m) => [m.elementId, m]))
  const elementScores = elements.map((el) => scoreElement(el, byId.get(el.id)))
  const rawTotal = elementScores.reduce((sum, e) => sum + e.points, 0)
  const forgottenPenaltyApplied = elementScores.some((e) => e.forgotten)
  const finalTotal = forgottenPenaltyApplied ? rawTotal / 2 : rawTotal
  const reviewedCount = elementScores.filter((e) => e.reviewed).length
  return {
    elementScores,
    rawTotal,
    forgottenPenaltyApplied,
    finalTotal,
    maxTotal,
    reviewedCount,
    elementCount: elements.length,
  }
}

/** Drop highest and lowest judge totals; sum the middle three (official panel). */
export function panelTotalFromFive(judgeTotals: number[]): number {
  if (judgeTotals.length !== 5) {
    throw new Error('panelTotalFromFive requires exactly 5 judge totals')
  }
  const sorted = [...judgeTotals].sort((a, b) => a - b)
  return sorted[1]! + sorted[2]! + sorted[3]!
}

export function setCriterionSeverity(
  mark: ElementMarkState,
  criterionId: string,
  severity: MistakeSeverity | null,
): ElementMarkState {
  const rest = mark.criteria.filter((c) => c.criterionId !== criterionId)
  const criteria =
    severity === null ? rest : [...rest, { criterionId, severity }]
  return {
    ...mark,
    reviewed: true,
    forgotten: false,
    criteria,
  }
}

export function setElementForgotten(
  mark: ElementMarkState,
  forgotten: boolean,
): ElementMarkState {
  if (forgotten) {
    return { ...mark, reviewed: true, forgotten: true, criteria: [] }
  }
  return { ...mark, reviewed: true, forgotten: false }
}

export function setElementComment(
  mark: ElementMarkState,
  comment: string,
): ElementMarkState {
  const next = comment.length > 0 ? comment : undefined
  return {
    ...mark,
    reviewed: mark.reviewed || Boolean(next?.trim()),
    comment: next,
  }
}

export function emptyElementMark(elementId: string): ElementMarkState {
  return { elementId, reviewed: false, forgotten: false, criteria: [] }
}
